import clsx from 'clsx'
import React, { PropsWithChildren, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { v4 as uuid } from 'uuid'
import { useAppTranslation } from '@hooks/useAppTranslation'
import { useEnvironment } from '@hooks/useEnvironment'
import { Button } from '@components/foundation/button/Button'
import { SVGIcon } from '@components/foundation/svg-icon/SVGIcon'
import { ThemeWrapper } from '@components/foundation/theme/ThemeWrapper'
import { injectDataAttributes } from '@utils/dataAttributes'
import styles from './styles/Modal.module.scss'

const MODAL_ROOT_ID = `directbilling-modal-root-${uuid()}`
const FLOATING_MODAL_ROOT_ID = `directbilling-floating-modal-root-${uuid()}`

export const ModalRootContainer: React.FC = () => {
  return (
    <>
      <div id={MODAL_ROOT_ID} />
      <div id={FLOATING_MODAL_ROOT_ID} />
    </>
  )
}

export type ModalProps = {
  open?: boolean
  onClose: () => void
} & PropsWithChildren

export const Modal: React.FC<ModalProps> = props => {
  const { open, onClose, children } = props
  const { brand } = useEnvironment()
  const containerRef = useRef<HTMLDivElement>(null)

  // close on escape key
  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose()
      }
    }

    document.addEventListener('keyup', onKeyUp)
    return () => document.removeEventListener('keyup', onKeyUp)
  }, [onClose, open])

  // close on click away from modal
  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (containerRef.current && e.target && !containerRef.current.contains(e.target as Node)) {
      onClose()
    }
  }

  const vh = document.documentElement.clientHeight * 0.01

  return (
    <>
      {open &&
        createPortal(
          <ThemeWrapper brand={brand}>
            <div className={clsx(styles.backdrop, styles.open)} onClick={onBackdropClick} style={{ '--vh': `${vh}px` }}>
              <div className={styles.container} ref={containerRef}>
                <button onClick={onClose} className={styles.closeButton} {...injectDataAttributes('modal.close')}>
                  <SVGIcon library="base" name="close" />
                </button>
                {children}
              </div>
            </div>
          </ThemeWrapper>,
          document.getElementById(MODAL_ROOT_ID) as HTMLDivElement
        )}
    </>
  )
}

export const FloatLeftModal: React.FC<ModalProps> = props => {
  const { open, onClose, children } = props
  const { brand } = useEnvironment()
  const containerRef = useRef<HTMLDivElement>(null)

  const vh = document.documentElement.clientHeight * 0.01

  return (
    <>
      {open &&
        createPortal(
          <ThemeWrapper brand={brand}>
            <div className={clsx(styles.backdrop, styles.open, styles.floatLeft)} style={{ '--vh': `${vh}px` }}>
              <div className={clsx(styles.container, styles.floatLeft)} ref={containerRef}>
                <button onClick={onClose} className={styles.closeButton} {...injectDataAttributes('modal.close')}>
                  <SVGIcon library="base" name="arrow-right" />
                </button>
                {children}
              </div>
            </div>
          </ThemeWrapper>,
          document.getElementById(FLOATING_MODAL_ROOT_ID) as HTMLDivElement
        )}
    </>
  )
}

type ModalSectionProps = PropsWithChildren & {
  className?: string
}

export const ModalHeader: React.FC<ModalSectionProps> = props => {
  const { className, children } = props
  return (
    <div className={clsx(styles.header, className)} {...injectDataAttributes('modal.header')}>
      {children}
    </div>
  )
}

export const ModalContent: React.FC<ModalSectionProps> = props => {
  const { className, children } = props
  return (
    <div className={clsx(styles.content, className)} {...injectDataAttributes('modal.content')}>
      {children}
    </div>
  )
}

type ModalFooterProps = ModalSectionProps & {
  buttonLabel?: string
}

export const ModalFooter: React.FC<ModalFooterProps> = props => {
  const { buttonLabel, className, children } = props
  const { t } = useAppTranslation()

  const onCloseClick = () => {
    const event = new KeyboardEvent('keyup', {
      key: 'Escape',
    })
    document.dispatchEvent(event)
  }

  return (
    <div className={clsx(styles.footer, className)} {...injectDataAttributes('modal.footer')}>
      {children}
      {!children && (
        <ModalActionContainer>
          <Button aria-label="close" onClick={onCloseClick} {...injectDataAttributes('modal.close')}>
            {buttonLabel ?? t('Common.Buttons.close')}
          </Button>
        </ModalActionContainer>
      )}
    </div>
  )
}

export const ModalActionContainer: React.FC<ModalSectionProps> = props => {
  const { className, children } = props
  return (
    <div className={clsx(styles.actionContainer, className)} {...injectDataAttributes('modal.actions')}>
      {children}
    </div>
  )
}
